import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Loader2,
  Stethoscope,
  Clock,
  CheckCircle2,
  XCircle,
  RefreshCw,
  FileText,
  Pill,
  ChevronRight,
  AlertCircle,
  Send,
} from 'lucide-react';
import { mdiService, MdiCase } from '@/services/mdiService';
import { useClinic } from '@/contexts/ClinicContext';
import { formatDistanceToNow, format } from 'date-fns';
import SmartPagination from '../ui/smart-pagination';
import { useToast } from '@/hooks/use-toast';

interface PatientMdiCasesTabProps {
  patientId: string;
  mdiAuthLink?: string | null;
  onViewEncounter?: (encounterId: string) => void;
  onViewPrescription?: (prescriptionId: string) => void;
}

const PatientMdiCasesTab = ({ 
  patientId, 
  onViewEncounter, 
  onViewPrescription 
}: PatientMdiCasesTabProps) => {
  const { clinic } = useClinic();
  const { toast } = useToast();
  const [cases, setCases] = useState<MdiCase[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [mdiEnabled, setMdiEnabled] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const ITEMS_PER_PAGE = 10;

  const fetchCases = async () => {
    if (!clinic?.id || !patientId) return;

    setIsLoading(true);
    try {
      // Check if MDI is enabled
      const enabled = await mdiService.isEnabled(clinic.id);
      setMdiEnabled(enabled);

      if (enabled) {
        const result = await mdiService.listCases({ patientId, clinicId: clinic.id });
        if (result.success && result.data) {
          setCases(result.data);
          setTotalPages(Math.ceil(result.data.length / ITEMS_PER_PAGE) || 1);
        }
      }
    } catch (error) {
      console.error('Error fetching MDI cases:', error);
      toast({
        title: 'Error',
        description: 'Failed to load MDI cases',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchCases();
  }, [clinic?.id, patientId]);

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const getStatusConfig = (status: MdiCase['status']) => {
    switch (status) {
      case 'pending':
        return { color: 'bg-amber-100 text-amber-700', label: 'Pending', icon: Clock };
      case 'submitted':
        return { color: 'bg-blue-100 text-blue-700', label: 'Submitted', icon: Send };
      case 'in_review':
        return { color: 'bg-indigo-100 text-indigo-700', label: 'In Review', icon: Clock };
      case 'completed':
        return { color: 'bg-emerald-100 text-emerald-700', label: 'Completed', icon: CheckCircle2 };
      case 'cancelled':
        return { color: 'bg-slate-100 text-slate-600', label: 'Cancelled', icon: XCircle };
      case 'failed':
        return { color: 'bg-red-100 text-red-700', label: 'Failed', icon: AlertCircle };
      default:
        return { color: 'bg-slate-100 text-slate-600', label: status || 'Unknown', icon: Clock };
    }
  };

  const getProgramLabel = (program: string) => {
    const programMap: Record<string, string> = {
      'weightwise': 'Weight Loss',
      'glp1': 'GLP-1',
      'trt': 'TRT',
      'hrt': 'HRT',
      'general': 'General',
    };
    return programMap[program] || program.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return format(date, 'MMM d, yyyy h:mm a');
    } catch {
      return dateString;
    }
  };

  const getRelativeTime = (dateString: string) => {
    try {
      return formatDistanceToNow(new Date(dateString), { addSuffix: true });
    } catch {
      return '';
    }
  };

  const isRefillCase = (mdiCase: MdiCase): boolean => {
    const caseType = ((mdiCase as any).caseType || (mdiCase as any).case_type || '').toString().toLowerCase();
    return caseType === 'refill';
  };

  // Get paginated cases
  const paginatedCases = cases.slice(
    (page - 1) * ITEMS_PER_PAGE,
    page * ITEMS_PER_PAGE
  );

  // If MDI is not enabled, show a message
  if (!isLoading && !mdiEnabled) {
    return (
      <div className="space-y-6">
        <div className="bg-white rounded-lg border border-slate-200">
          <div className="px-5 py-4 border-b border-slate-100">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-indigo-500 flex items-center justify-center">
                <Stethoscope className="h-4 w-4 text-white" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-slate-900">MDI Telehealth Cases</h3>
                <p className="text-xs text-slate-500">MDI integration is not enabled for this clinic</p>
              </div>
            </div>
          </div>
          <div className="p-5">
            <div className="text-center py-12">
              <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-3">
                <Stethoscope className="h-6 w-6 text-slate-400" />
              </div>
              <p className="text-sm text-slate-500">MDI integration is not enabled</p>
              <p className="text-xs text-slate-400 mt-1">Contact your administrator to enable MDI</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Render cases list content
  const renderCasesList = () => (
    <>
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-indigo-500" />
          <span className="ml-2 text-sm text-slate-500">Loading MDI cases...</span>
        </div>
      ) : cases.length === 0 ? (
        <div className="text-center py-12">
          <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-3">
            <Stethoscope className="h-6 w-6 text-slate-400" />
          </div>
          <p className="text-sm text-slate-500">No MDI cases found</p>
          <p className="text-xs text-slate-400 mt-1">Cases will appear here when intakes are sent to MDI</p>
        </div>
      ) : (
        <div className="space-y-3">
          {paginatedCases.map((mdiCase) => {
            const statusConfig = getStatusConfig(mdiCase.status);
            const StatusIcon = statusConfig.icon;
            
            return (
              <div
                key={mdiCase.id}
                className="border border-slate-200 rounded-lg p-4 bg-white hover:border-slate-300 transition-colors"
              >
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                  {/* Case Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-lg bg-indigo-50 flex items-center justify-center flex-shrink-0">
                        <Stethoscope className="h-5 w-5 text-indigo-500" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge className={`${statusConfig.color} font-medium text-xs`}>
                            <StatusIcon className="h-3 w-3 mr-1" />
                            {statusConfig.label}
                          </Badge>
                          <Badge variant="outline" className="text-slate-600 border-slate-200 text-xs">
                            {getProgramLabel(mdiCase.program)}
                          </Badge>
                          {isRefillCase(mdiCase) && (
                            <Badge variant="outline" className="bg-violet-50 text-violet-700 border-violet-200 text-xs">
                              Refill
                            </Badge>
                          )}
                        </div>
                        
                        <div className="mt-2 space-y-1">
                          <div className="flex items-center gap-2 text-xs text-slate-500 flex-wrap">
                            <span>Source: {mdiCase.source === 'clinic_portal' ? 'Clinic Portal' : mdiCase.source}</span>
                            {mdiCase.mdiCaseId && (
                              <>
                                <span>•</span>
                                <span className="font-mono text-slate-600">MDI #{mdiCase.mdiCaseId}</span>
                              </>
                            )}
                            {(mdiCase.prescriptionCount ?? 0) > 0 && (
                              <>
                                <span>•</span>
                                <span className="text-emerald-600 font-medium flex items-center gap-1">
                                  <Pill className="w-3 h-3" />
                                  {mdiCase.prescriptionCount} prescription{mdiCase.prescriptionCount !== 1 ? 's' : ''} created
                                </span>
                              </>
                            )}
                          </div>
                          <div className="text-xs text-slate-400">
                            Created {formatDate(mdiCase.createdAt)}
                            {mdiCase.completedAt && (
                              <span className="ml-2">• Completed {formatDate(mdiCase.completedAt)}</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Right side - Time and Actions */}
                  <div className="flex flex-col items-end gap-2">
                    <span className="text-xs text-slate-400">
                      {getRelativeTime(mdiCase.createdAt)}
                    </span>
                    
                    {/* Actions for completed cases */}
                    {mdiCase.status === 'completed' && (
                      <div className="flex items-center gap-2">
                        {mdiCase.encounterId && onViewEncounter && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => onViewEncounter(mdiCase.encounterId!)}
                            className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 h-7 px-2 text-xs"
                          >
                            <FileText className="w-3 h-3 mr-1" />
                            Encounter
                            <ChevronRight className="w-3 h-3 ml-1" />
                          </Button>
                        )}
                        {mdiCase.prescriptionId && onViewPrescription && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => onViewPrescription(mdiCase.prescriptionId!)}
                            className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 h-7 px-2 text-xs"
                          >
                            <Pill className="w-3 h-3 mr-1" />
                            Prescription
                            <ChevronRight className="w-3 h-3 ml-1" />
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Status message for in-progress cases */}
                {mdiCase.status === 'in_review' && (
                  <div className="flex items-center gap-2 mt-3 pt-3 border-t border-slate-100 text-xs text-indigo-600">
                    <Clock className="w-3 h-3 animate-pulse" />
                    MDI clinician is reviewing this case
                  </div>
                )}

                {/* Error message for failed cases */}
                {mdiCase.status === 'failed' && (
                  <div className="flex items-center gap-2 mt-3 pt-3 border-t border-slate-100 text-xs text-red-600">
                    <AlertCircle className="w-3 h-3" />
                    Case submission failed. Please try again.
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {!isLoading && cases.length > ITEMS_PER_PAGE && (
        <div className="pt-4 border-t border-slate-100 mt-4">
          <SmartPagination
            currentPage={page}
            totalPages={totalPages}
            onPageChange={handlePageChange}
          />
        </div>
      )}
    </>
  );

  return (
    <div className="space-y-6">
      {/* Main Card */}
      <div className="bg-white rounded-lg border border-slate-200">
        {/* Header */}
        <div className="px-5 py-4 border-b border-slate-100">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-indigo-500 flex items-center justify-center">
                <Stethoscope className="h-4 w-4 text-white" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-slate-900">MDI Telehealth</h3>
                <p className="text-xs text-slate-500">
                  {isLoading ? 'Loading...' : `${cases.length} case${cases.length !== 1 ? 's' : ''} found`}
                </p>
              </div>
            </div>

            <Button
              variant="outline"
              size="sm"
              onClick={fetchCases}
              className="border-slate-200"
              disabled={isLoading}
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>

        <div className="p-5">
          {renderCasesList()}
        </div>
      </div>
    </div>
  );
};

export default PatientMdiCasesTab;
